import type { TelegramWebApp, ColorScheme } from './types'

/**
 * Единственная точка доступа к Telegram WebApp.
 * Приложение никогда не трогает window.Telegram напрямую -
 * иначе оно падает вне Telegram и не тестируется.
 *
 * Вне Telegram отдаём mock: приложение полностью работает в браузере.
 */

let cached: TelegramWebApp | null | undefined

/**
 * Хранилище для mock-режима.
 *
 * Пишет в localStorage, чтобы прогресс переживал перезагрузку страницы
 * при разработке. Но localStorage не везде доступен: приватный режим
 * Safari бросает исключение на запись, в тестовой среде его может
 * не быть вовсе. Поэтому источником правды служит Map в памяти,
 * а localStorage - зеркалом для сохранения между сессиями.
 *
 * Молча глотать ошибку записи нельзя: прогресс терялся бы незаметно.
 */
const MOCK_PREFIX = 'brainhone.mock.'
const mockMemory = new Map<string, string>()

function lsRead(key: string): string | null {
  try {
    return localStorage.getItem(MOCK_PREFIX + key)
  } catch {
    return null
  }
}

function lsWrite(key: string, value: string): void {
  try {
    localStorage.setItem(MOCK_PREFIX + key, value)
  } catch {
    // недоступен - остаёмся на памяти
  }
}

function mockCloudStorage(): NonNullable<TelegramWebApp['CloudStorage']> {
  return {
    setItem(key, value, cb) {
      mockMemory.set(key, value)
      lsWrite(key, value)
      cb?.(null, true)
    },
    getItem(key, cb) {
      const inMemory = mockMemory.get(key)
      if (inMemory !== undefined) {
        cb(null, inMemory)
        return
      }
      const stored = lsRead(key)
      if (stored !== null) mockMemory.set(key, stored)
      cb(null, stored ?? '')
    },
    removeItem(key, cb) {
      mockMemory.delete(key)
      try {
        localStorage.removeItem(MOCK_PREFIX + key)
      } catch {
        // не критично
      }
      cb?.(null, true)
    },
    getKeys(cb) {
      const keys = new Set(mockMemory.keys())
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i)
          if (k?.startsWith(MOCK_PREFIX)) keys.add(k.slice(MOCK_PREFIX.length))
        }
      } catch {
        // не критично
      }
      cb(null, [...keys])
    },
  }
}

/** Только для тестов - очистить mock-хранилище. */
export function resetMockStorage(): void {
  mockMemory.clear()
}

function createMock(): TelegramWebApp {
  const prefersDark =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches

  return {
    initData: '',
    initDataUnsafe: {},
    version: 'mock',
    platform: 'browser',
    colorScheme: prefersDark ? 'dark' : 'light',
    themeParams: {},
    isExpanded: true,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
    viewportStableHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
    ready: () => {},
    expand: () => {},
    close: () => {},
    onEvent: () => {},
    offEvent: () => {},
    CloudStorage: mockCloudStorage(),
  }
}

/**
 * Реальный WebApp, если мы действительно внутри Telegram. Иначе null.
 *
 * Важно: скрипт telegram-web-app.js создаёт window.Telegram.WebApp
 * в ЛЮБОМ браузере, и initData там существует (просто пустая строка).
 * Проверять её наличие бесполезно - вне Telegram мы бы ушли в настоящий
 * CloudStorage, чьи колбэки никогда не вызываются, и приложение зависло бы.
 *
 * Надёжный признак - platform: вне Telegram скрипт ставит 'unknown'.
 */
export function getRealWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null
  const app = window.Telegram?.WebApp
  if (!app) return null
  const platform = app.platform
  if (!platform || platform === 'unknown') return null
  return app
}

/** WebApp или mock. Всегда возвращает рабочий объект. */
export function getWebApp(): TelegramWebApp {
  if (cached === undefined) cached = getRealWebApp()
  return cached ?? createMock()
}

export function isInTelegram(): boolean {
  return getRealWebApp() !== null
}

/** Инициализация: ready + expand + фиксация вьюпорта. Вызывать один раз. */
export function initTelegram(): void {
  const app = getWebApp()
  app.ready()
  app.expand()
  app.disableVerticalSwipes?.()
}

export function getColorScheme(): ColorScheme {
  return getWebApp().colorScheme
}

export function getLanguageCode(): string | undefined {
  return getWebApp().initDataUnsafe.user?.language_code
}

export function getUserFirstName(): string | undefined {
  return getWebApp().initDataUnsafe.user?.first_name
}

/** Только для тестов - сбросить закешированный инстанс. */
export function resetWebAppCache(): void {
  cached = undefined
}

/** Сырая initData для проверки подписи на сервере. Вне Telegram пусто. */
export function getInitData(): string {
  return getWebApp().initData
}

/**
 * Запросить разрешение писать пользователю.
 *
 * Telegram не даёт боту писать первым без согласия. Старые клиенты
 * метода не знают - там считаем, что разрешения нет, вместо того
 * чтобы делать вид, будто всё в порядке.
 */
export function requestWriteAccess(): Promise<boolean> {
  return new Promise((resolve) => {
    const app = getWebApp()
    if (!app.requestWriteAccess) {
      resolve(false)
      return
    }
    try {
      app.requestWriteAccess((granted) => resolve(granted === true))
    } catch {
      resolve(false)
    }
  })
}

/** Часовой пояс и смещение для напоминаний по местному времени. */
export function getTimeZone(): { tz: string; offset: number } {
  let tz = 'UTC'
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    // редкая среда без Intl - останемся на смещении
  }
  // getTimezoneOffset возвращает минуты со знаком наоборот
  return { tz, offset: -new Date().getTimezoneOffset() }
}
